/* eslint-disable react/no-array-index-key */
/** @jsx jsx */
import { jsx, Container, Box } from 'theme-ui';
import React, { FunctionComponent } from 'react';
import { graphql, PageProps } from 'gatsby';
import Modal from 'react-modal';

import Layout from '@codersclan/gatsby-theme-airfleet/src/components/layout/layout';
import SectionsLoader from '@codersclan/gatsby-theme-airfleet/src/components/sectionsLoader';

// Types
import { PageDetails as PageDetailsType } from '../../@codersclan/gatsby-theme-airfleet/@types/page';
import { CustomerDetails } from '../../@types/customers';

// Components
import CustomGrid from '../../components/customGrid';
import Card from '../../components/card';
import CTAButton from '../../components/ctaButton';
import Head from '../../components/head';

// Hooks
import useCustomerDefaultImage from '../../hooks/useCustomerDefaultImage';
import usePagination from '../../hooks/usePagination';
import useSEOPropsResolver from '../../hooks/useSEOPropsResolver';

// Helpers
import SearchSectionHelper from '../../helpers/searchSectionHelper';
import merge from 'lodash.merge';

Modal.setAppElement('#___gatsby');

interface Data {
  pagesData: {
    nodes: Array<PageDetailsType>;
  };
  customersData: {
    nodes: Array<CustomerDetails>;
  };
  allWp: {
    nodes: Array<{
      themeGeneralSettings: {
        optionsGeneral: {
          metaTags: {
            value: string;
          };
        };
      };
    }>;
  };
}

type Props = PageProps<Data>;

function getUrl(customer: CustomerDetails): string | undefined {
  return (
    customer?.templateCustomers?.customerSection?.data?.[0]?.link?.url ||
    customer?.templateCustomers?.customerSection?.data?.[0]?.mediaSelector
      ?.localFile?.publicURL
  );
}

const CustomersPage: FunctionComponent<Props> = ({ data }) => {
  const { limit, resetPagination, loadMore } = usePagination();
  const { pagesData, customersData, allWp } = data;
  const page = pagesData.nodes[0];
  const sections = page?.templateAllPages?.sections || [];
  const customerDefaultImage = useCustomerDefaultImage();

  React.useEffect(() => {
    resetPagination();
  }, [customersData]);

  // Memos
  const headerSection = React.useMemo(
    () =>
      SearchSectionHelper.searchSectionHelper(
        sections,
        SearchSectionHelper.SECTIONS.HERO_IMAGE,
      ),
    [sections],
  );

  const [headingTextreaPersoncompanyDataBg] = React.useMemo(
    () =>
      SearchSectionHelper.searchSectionHelper(
        sections,
        SearchSectionHelper.SECTIONS.HEADING_TEXTAREA_PERSONCOMPANY_DATA_BG,
      ),
    [sections],
  );

  const prefooterCTASection = React.useMemo(
    () =>
      SearchSectionHelper.searchSectionHelper(
        sections,
        SearchSectionHelper.SECTIONS.PREFOOTER_CTA,
      ),
    [sections],
  );

  const [firstSix, others] = React.useMemo(
    () => [customersData.nodes.slice(0, 6), customersData.nodes.slice(6)],
    [customersData],
  );

  const [othersList, showMore] = React.useMemo(
    () => [others.slice(0, limit), limit < others.length],
    [limit, others],
  );

  const { metaTags } = allWp?.nodes[0]?.themeGeneralSettings?.optionsGeneral;
  const seoProps = metaTags
    ? merge({}, useSEOPropsResolver(page), { metaTags: metaTags.value })
    : useSEOPropsResolver(page);

  return (
    <Layout>
      <Head {...seoProps} />
      {/* Top Sections */}
      <SectionsLoader sections={headerSection} />

      {/* Custom Section */}
      <Container sx={{ padding: ['40px 33px 0px', '82px 0px 0px'] }}>
        <CustomGrid>
          {firstSix.map((p, idx) => (
            <Card
              key={`Customer_${idx}`}
              title={p.title}
              content={p.content}
              cta={{
                label: '',
                url: getUrl(p),
                target: '_self',
              }}
              image={
                p.featuredImage?.node || {
                  localFile: customerDefaultImage.fluid,
                }
              }
            />
          ))}
        </CustomGrid>
      </Container>

      {/* Middle Section */}
      {headingTextreaPersoncompanyDataBg && (
        <SectionsLoader sections={[headingTextreaPersoncompanyDataBg]} />
      )}

      {/* Custom Section */}
      {othersList.length > 0 && (
        <Container
          sx={{
            marginTop: !headingTextreaPersoncompanyDataBg
              ? ['43px', '84px']
              : '0px',
            paddingX: ['33px', '0px'],
          }}
        >
          <CustomGrid>
            {othersList.map((p, idx) => (
              <Card
                key={`Customer_${idx}`}
                title={p.title}
                content={p.content}
                cta={{
                  label: '',
                  url: getUrl(p),
                  target: '_self',
                }}
                image={
                  p.featuredImage?.node || {
                    localFile: customerDefaultImage.fluid,
                  }
                }
              />
            ))}
          </CustomGrid>
          {showMore && (
            <CTAButton
              key={othersList.length}
              type="button"
              alternative
              sx={{ margin: ['57px auto 0px', '84px auto 0px'] }}
              onClick={loadMore}
            >
              Load More
            </CTAButton>
          )}
        </Container>
      )}

      <Box sx={{ marginTop: ['49px', '80px'] }}>
        {/* Bottom Sections */}
        <SectionsLoader sections={prefooterCTASection} />
      </Box>
    </Layout>
  );
};

export default CustomersPage;

export const pageQuery: void = graphql`
  query($slug: String) {
    allWp {
      nodes {
        themeGeneralSettings {
          optionsGeneral {
            metaTags {
              value
            }
          }
        }
      }
    }
    pagesData: allWpPage(filter: { slug: { eq: $slug } }) {
      nodes {
        ...PageDetailsWithSEOFragment
        templateAllPages {
          sections {
            ...HeroImageFragment
            ...HeadingTextareaPersoncompanyDataBgFragment
            ...PreFooterCTAFragment
          }
        }
      }
    }
    customersData: allWpCustomer(sort: { fields: date, order: DESC }) {
      nodes {
        uri
        templateCustomers {
          customerSection {
            data {
              ... on WpCustomer_Templatecustomers_CustomerSection_Data_PdfFile {
                chooseTheMethod
                link {
                  url
                }
                mediaSelector {
                  id
                  sourceUrl
                  mediaItemUrl
                  localFile {
                    id
                    publicURL
                  }
                }
              }
            }
          }
        }
        title
        content
        featuredImage {
          node {
            ...FluidImageFragmentWide
          }
        }
      }
    }
  }
`;
